/* eslint-disable no-underscore-dangle */
import { Chart, registerables } from 'chart.js';
import consumer from 'channels/consumer';
import ApplicationController from './application_controller';

Chart.register(...registerables);

const MAX_DATA_POINTS = 360; // ~1 hour at 10s intervals

export default class extends ApplicationController {
  static targets = [
    'activeUsers',
    'activeAmbikiStaff',
    'roomSessions',
    'teletherapySessions',
    'jobsPending',
    'jobsWorking',
    'jobsFailed',
    'recentVisits',
    'recentErrors',
    'updatedAt',
    'connectionStatus',
    'sessionsChart',
    'jobsChart',
  ];

  initialize() {
    this.timestamps = [];
    this.history = {
      active_users: [],
      active_ambiki_staff: [],
      room_sessions: [],
      teletherapy_sessions: [],
      recent_visits: [],
      recent_errors: [],
      jobs_pending: [],
      jobs_working: [],
      jobs_failed: [],
    };

    this.subscription = consumer.subscriptions.create(
      { channel: 'LiveActivityChannel' },
      {
        connected: this._connected.bind(this),
        disconnected: this._disconnected.bind(this),
        received: this.received.bind(this),
      }
    );
  }

  connect() {
    this._initCharts();
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.sessionsChart) this.sessionsChart.destroy();
    if (this.jobsChart) this.jobsChart.destroy();
  }

  _connected() {
    if (this.hasConnectionStatusTarget) {
      this.connectionStatusTarget.textContent = '';
      this.connectionStatusTarget.classList.add('d-none');
    }
  }

  _disconnected() {
    if (this.hasConnectionStatusTarget) {
      this.connectionStatusTarget.textContent = 'Disconnected — reconnecting\u2026';
      this.connectionStatusTarget.classList.remove('d-none');
    }
  }

  received(data) {
    this.activeUsersTarget.textContent = data.active_users;
    this.activeAmbikiStaffTarget.textContent = data.active_ambiki_staff;
    this.roomSessionsTarget.textContent = data.room_sessions;
    this.teletherapySessionsTarget.textContent = data.teletherapy_sessions;
    this.jobsPendingTarget.textContent = data.jobs_pending;
    this.jobsWorkingTarget.textContent = data.jobs_working;
    this.jobsFailedTarget.textContent = data.jobs_failed;
    this.recentVisitsTarget.textContent = data.recent_visits;
    this.recentErrorsTarget.textContent = data.recent_errors;
    this.updatedAtTarget.textContent = data.updated_at;

    this._recordDataPoint(data);
  }

  _recordDataPoint(data) {
    this.timestamps.push(data.updated_at);
    this.history.active_users.push(data.active_users);
    this.history.active_ambiki_staff.push(data.active_ambiki_staff);
    this.history.room_sessions.push(data.room_sessions);
    this.history.teletherapy_sessions.push(data.teletherapy_sessions);
    this.history.recent_visits.push(data.recent_visits);
    this.history.recent_errors.push(data.recent_errors);
    this.history.jobs_pending.push(data.jobs_pending);
    this.history.jobs_working.push(data.jobs_working);
    this.history.jobs_failed.push(data.jobs_failed);

    if (this.timestamps.length > MAX_DATA_POINTS) {
      this.timestamps.shift();
      Object.values(this.history).forEach((arr) => arr.shift());
    }

    this._updateCharts();
  }

  _initCharts() {
    if (this.hasSessionsChartTarget) {
      const ctx = this.sessionsChartTarget.getContext('2d');
      this.sessionsChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: [],
          datasets: [
            this._dataset('Active Users', '#007bff', 'active_users'),
            this._dataset('Ambiki Staff', '#3cceb7', 'active_ambiki_staff'),
            this._dataset('Teletherapy V1', '#dc3545', 'room_sessions'),
            this._dataset('Teletherapy V2', '#fd7e14', 'teletherapy_sessions'),
            this._dataset('Recent Visitors', '#6c757d', 'recent_visits'),
            this._dataset('Errors (1h)', '#e83e8c', 'recent_errors'),
          ],
        },
        options: this._chartOptions({ logarithmic: true }),
      });
    }

    if (this.hasJobsChartTarget) {
      const ctx = this.jobsChartTarget.getContext('2d');
      this.jobsChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: [],
          datasets: [
            this._dataset('Pending', '#ffc107', 'jobs_pending'),
            this._dataset('Working', '#007bff', 'jobs_working'),
            this._dataset('Failed', '#dc3545', 'jobs_failed'),
          ],
        },
        options: this._chartOptions(),
      });
    }
  }

  _dataset(label, color, key) {
    return {
      label,
      data: [],
      borderColor: color,
      backgroundColor: `${color}20`,
      borderWidth: 2,
      fill: false,
      tension: 0.3,
      pointRadius: 0,
      pointHitRadius: 10,
      _key: key,
    };
  }

  _chartOptions({ logarithmic = false } = {}) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300 },
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        title: { display: false },
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            padding: 20,
            font: { size: 13 },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          titleColor: 'white',
          bodyColor: 'white',
        },
      },
      scales: {
        y: {
          type: logarithmic ? 'logarithmic' : 'linear',
          beginAtZero: !logarithmic,
          grid: { color: 'rgba(0,0,0,0.1)' },
        },
        x: {
          grid: { color: 'rgba(0,0,0,0.05)' },
          ticks: { maxTicksLimit: 15 },
        },
      },
    };
  }

  _updateCharts() {
    [this.sessionsChart, this.jobsChart].forEach((chart) => {
      if (!chart) return;

      chart.data.labels = this.timestamps;
      chart.data.datasets.forEach((dataset) => {
        dataset.data = this.history[dataset._key];
      });
      chart.update('none');
    });
  }
}
